import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { VacationRequest } from '../../types';
import { formatShortDate } from '../../utils/formatters';

interface VacationCancelDialogProps {
  request: VacationRequest | null;
  open: boolean;
  isSubmitting?: boolean;
  onOpenChange: (open: boolean) => void;
  /** Confirm cancellation, passing the optional reason (trimmed, may be empty). */
  onConfirm: (reason: string) => void;
}

/**
 * Confirmation modal for cancelling a vacation request. Offers an optional
 * free-text reason that is forwarded to the cancel mutation.
 */
export const VacationCancelDialog: React.FC<VacationCancelDialogProps> = ({
  request,
  open,
  isSubmitting,
  onOpenChange,
  onConfirm,
}) => {
  const { t } = useTranslation('vacations');
  const [reason, setReason] = useState('');

  // Reset the field each time the dialog is opened for a request.
  useEffect(() => {
    if (open) setReason('');
  }, [open, request?.id]);

  if (!request) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="vacation-cancel-dialog">
        <DialogHeader>
          <DialogTitle>{t('cancelDialog.title')}</DialogTitle>
          <DialogDescription>{t('cancelDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            {formatShortDate(request.startDate)} – {formatShortDate(request.endDate)}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cancel-reason">{t('cancelDialog.reasonLabel')}</Label>
            <Textarea
              id="cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('cancelDialog.reasonPlaceholder')}
              data-testid="vacation-cancel-reason"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            data-testid="vacation-cancel-back"
          >
            {t('cancelDialog.back')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(reason.trim())}
            disabled={isSubmitting}
            data-testid="vacation-cancel-confirm"
          >
            {t('cancelDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
